import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Ban,
  CalendarClock,
  CalendarX,
  Copy,
  Edit,
  ExternalLink,
  Mail,
  MessageCircle,
  Phone,
  Video,
  Plus,
  Search,
  Trash2,
  Users
} from 'lucide-react';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import GoogleCalendarPanel from './GoogleCalendarPanel';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm, usePrompt } from '../../contexts/ConfirmContext';

interface AgendamentoLinkItem {
  _id: string;
  title: string;
  publicTitle: string;
  publicUrl: string;
  status: 'active' | 'expired' | 'revoked';
  expiresAt: string;
  avatar?: { _id: string; name: string; photoUrl?: string } | null;
  availability: { startDate: string; endDate: string; durationMin: number; timezone: string };
  reservasCount: number;
  upcomingCount: number;
}

interface ReservaItem {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  whatsapp?: string;
  startAt: string;
  endAt: string;
  status: 'confirmed' | 'cancelled';
  cancelledBy?: 'client' | 'admin' | 'reschedule';
  meetUrl?: string;
  link?: { _id: string; title: string; availability?: { timezone: string } } | null;
}

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  expired: 'bg-gray-100 text-gray-700',
  revoked: 'bg-red-100 text-red-800',
  confirmed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-700'
};

const formatCivilDate = (ymd: string, lang: string) =>
  new Date(`${ymd}T12:00:00`).toLocaleDateString(lang, { day: '2-digit', month: 'short' });

const whatsappDigits = (phone: string) => phone.replace(/\D/g, '');

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function buildWhatsappMessage(
  t: (k: string, o?: Record<string, unknown>) => string,
  link: { publicTitle: string; publicUrl: string; expiresAt: string },
  lang: string
) {
  return t('agendamentoAdmin.whatsappMessage', {
    title: link.publicTitle,
    url: link.publicUrl,
    date: new Date(link.expiresAt).toLocaleDateString(lang),
    interpolation: { escapeValue: false }
  });
}

const AdminAgendamentos: React.FC = () => {
  const { t, i18n } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'reservas' ? 'reservas' : 'links';
  const linkFilter = searchParams.get('link') || '';

  const [links, setLinks] = useState<AgendamentoLinkItem[]>([]);
  const [reservas, setReservas] = useState<ReservaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [when, setWhen] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [status, setStatus] = useState<'' | 'confirmed' | 'cancelled'>('confirmed');

  const setTab = (next: 'links' | 'reservas', link?: string) => {
    const params: Record<string, string> = { tab: next };
    if (link) params.link = link;
    setSearchParams(params);
  };

  const fetchLinks = useCallback(async () => {
    const params = new URLSearchParams({ limit: '50', ...(search && { search }) });
    const res = await axios.get(`/agendamento/links?${params}`);
    setLinks(res.data.links);
  }, [search]);

  const fetchReservas = useCallback(async () => {
    const params = new URLSearchParams({ limit: '100', when });
    if (status) params.set('status', status);
    if (linkFilter) params.set('link', linkFilter);
    if (search) params.set('search', search);
    const res = await axios.get(`/agendamento/reservas?${params}`);
    setReservas(res.data.reservas);
  }, [when, status, linkFilter, search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // A aba de reservas também precisa dos links (filtro por link)
        await Promise.all([fetchLinks(), tab === 'reservas' ? fetchReservas() : Promise.resolve()]);
      } catch (error) {
        console.error('Erro ao carregar agendamentos:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab, fetchLinks, fetchReservas]);

  const notifyError = (error: any) =>
    notifications.showError(t('agendamentoAdmin.error'), error.response?.data?.message || t('agendamentoAdmin.tryAgain'));

  const handleCopy = async (text: string, msgKey: string) => {
    if (await copyText(text)) notifications.showSuccess(t('agendamentoAdmin.copied'), t(msgKey));
  };

  const handleRevoke = async (link: AgendamentoLinkItem) => {
    if (!(await confirm({ title: t('agendamentoAdmin.revoke'), message: t('agendamentoAdmin.revokeConfirm', { title: link.title }), confirmText: t('agendamentoAdmin.revoke'), variant: 'danger' }))) return;
    try {
      await axios.post(`/agendamento/links/${link._id}/revoke`);
      setLinks((prev) => prev.map((l) => (l._id === link._id ? { ...l, status: 'revoked' } : l)));
      notifications.showSuccess(t('agendamentoAdmin.revoked'), '');
    } catch (error) {
      notifyError(error);
    }
  };

  const handleDelete = async (link: AgendamentoLinkItem) => {
    if (!(await confirm({ message: t('agendamentoAdmin.deleteConfirm', { title: link.title }), confirmText: t('common.delete'), variant: 'danger' }))) return;
    try {
      await axios.delete(`/agendamento/links/${link._id}`);
      setLinks((prev) => prev.filter((l) => l._id !== link._id));
      notifications.showSuccess(t('agendamentoAdmin.deleted'), '');
    } catch (error) {
      notifyError(error);
    }
  };

  const handleCancelReserva = async (r: ReservaItem) => {
    const reason = await prompt({
      title: t('agendamentoAdmin.cancelReserva'),
      message: t('agendamentoAdmin.cancelReservaConfirm', { nome: `${r.firstName} ${r.lastName}` }),
      label: t('agendamentoAdmin.cancelReason'),
      multiline: true,
      confirmText: t('agendamentoAdmin.cancelReserva'),
      cancelText: t('common.back'),
      variant: 'danger'
    });
    if (reason === null) return;
    try {
      await axios.post(`/agendamento/reservas/${r._id}/cancel`, { reason, notifyClient: true });
      setReservas((prev) => prev.map((x) => (x._id === r._id ? { ...x, status: 'cancelled', cancelledBy: 'admin' } : x)));
      notifications.showSuccess(t('agendamentoAdmin.reservaCancelled'), '');
    } catch (error) {
      notifyError(error);
    }
  };

  const statusLabel = (s: string) =>
    ({
      active: t('agendamentoAdmin.statusActive'),
      expired: t('agendamentoAdmin.statusExpired'),
      revoked: t('agendamentoAdmin.statusRevoked'),
      confirmed: t('agendamentoAdmin.statusConfirmed'),
      cancelled: t('agendamentoAdmin.statusCancelled')
    })[s] || s;

  const cancelledByLabel = (by?: string) =>
    by === 'client'
      ? t('agendamentoAdmin.cancelledByClient')
      : by === 'admin'
        ? t('agendamentoAdmin.cancelledByAdmin')
        : by === 'reschedule'
          ? t('agendamentoAdmin.cancelledByReschedule')
          : '';

  const dateTimeFmt = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
    }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('agendamentoAdmin.title')}</h1>
              <p className="text-gray-600">{t('agendamentoAdmin.subtitle')}</p>
            </div>
            <Link to="/admin/agendamentos/new" className="btn-primary inline-flex items-center space-x-2 self-start">
              <Plus className="w-5 h-5" />
              <span>{t('agendamentoAdmin.newLink')}</span>
            </Link>
          </div>
          <div className="mt-5 flex gap-2">
            <button type="button" className={tabClass(tab === 'links')} onClick={() => setTab('links')}>
              {t('agendamentoAdmin.tabLinks')}
            </button>
            <button type="button" className={tabClass(tab === 'reservas')} onClick={() => setTab('reservas', linkFilter)}>
              {t('agendamentoAdmin.tabReservas')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {tab === 'links' && <GoogleCalendarPanel />}
        <div className="bg-white rounded-lg shadow mb-6 p-4 sm:p-6 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t('common.search') as string}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          {tab === 'reservas' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:w-[36rem]">
              <select
                value={linkFilter}
                onChange={(e) => setTab('reservas', e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">{t('agendamentoAdmin.filterAllLinks')}</option>
                {links.map((l) => (
                  <option key={l._id} value={l._id}>
                    {l.title}
                  </option>
                ))}
              </select>
              <select value={when} onChange={(e) => setWhen(e.target.value as typeof when)} className="border border-gray-300 rounded-lg px-3 py-2">
                <option value="upcoming">{t('agendamentoAdmin.filterUpcoming')}</option>
                <option value="past">{t('agendamentoAdmin.filterPast')}</option>
                <option value="all">{t('agendamentoAdmin.filterAll')}</option>
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="border border-gray-300 rounded-lg px-3 py-2">
                <option value="confirmed">{t('agendamentoAdmin.statusConfirmed')}</option>
                <option value="cancelled">{t('agendamentoAdmin.statusCancelled')}</option>
                <option value="">{t('agendamentoAdmin.filterAllStatus')}</option>
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('common.loading')}</p>
          </div>
        ) : tab === 'links' ? (
          links.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <CalendarClock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('agendamentoAdmin.emptyLinks')}</h3>
              <Link to="/admin/agendamentos/new" className="btn-primary">
                {t('agendamentoAdmin.newLink')}
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link._id} className="bg-white rounded-lg shadow p-4 sm:p-5">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {link.avatar?.photoUrl ? (
                        <img src={link.avatar.photoUrl} alt={link.avatar.name} className="h-10 w-10 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center shrink-0">
                          <CalendarClock className="w-5 h-5" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-gray-900">{link.title}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE[link.status]}`}>
                            {statusLabel(link.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{link.publicTitle}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatCivilDate(link.availability.startDate, i18n.language)} –{' '}
                          {formatCivilDate(link.availability.endDate, i18n.language)} · {link.availability.durationMin} min
                          {link.avatar ? ` · ${link.avatar.name}` : ''}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setTab('reservas', link._id)}
                      className="inline-flex items-center gap-2 text-sm text-gray-700 hover:text-primary-700 self-start md:self-center"
                      title={t('agendamentoAdmin.viewReservas') as string}
                    >
                      <Users className="w-4 h-4" />
                      {t('agendamentoAdmin.reservasCount', { upcoming: link.upcomingCount, total: link.reservasCount })}
                    </button>

                    <div className="flex items-center gap-1 flex-wrap">
                      {link.status === 'active' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleCopy(link.publicUrl, 'agendamentoAdmin.linkCopied')}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title={t('agendamentoAdmin.copyLink') as string}
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleCopy(buildWhatsappMessage(t, link, i18n.language), 'agendamentoAdmin.whatsappCopied')}
                            className="p-2 text-green-700 hover:bg-green-50 rounded-lg"
                            title={t('agendamentoAdmin.copyWhatsapp') as string}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </button>
                          <a
                            href={link.publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title={link.publicUrl}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </>
                      )}
                      <Link
                        to={`/admin/agendamentos/${link._id}/edit`}
                        className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg"
                        title={t('admin.edit') as string}
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      {link.status !== 'revoked' && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(link)}
                          className="p-2 text-amber-700 hover:bg-amber-50 rounded-lg"
                          title={t('agendamentoAdmin.revoke') as string}
                        >
                          <Ban className="w-4 h-4" />
                        </button>
                      )}
                      {link.reservasCount === 0 && (
                        <button
                          type="button"
                          onClick={() => handleDelete(link)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                          title={t('common.delete') as string}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )
        ) : reservas.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CalendarX className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">{t('agendamentoAdmin.emptyReservas')}</h3>
          </div>
        ) : (
          <ul className="space-y-3">
            {reservas.map((r) => (
              <li key={r._id} className="bg-white rounded-lg shadow p-4 sm:p-5">
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  <div className="md:w-56 shrink-0">
                    <p className={`font-semibold first-letter:uppercase ${r.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {dateTimeFmt.format(new Date(r.startAt))}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{r.link?.title}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">
                      {r.firstName} {r.lastName}
                    </p>
                    <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 hover:text-primary-700 break-all">
                        <Mail className="w-4 h-4 shrink-0" />
                        {r.email}
                      </a>
                      {r.status === 'confirmed' && r.meetUrl && (
                        <a
                          href={r.meetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 hover:text-primary-700"
                        >
                          <Video className="w-4 h-4 shrink-0" />
                          Meet
                        </a>
                      )}
                      {r.whatsapp && (
                        <a
                          href={`https://wa.me/${whatsappDigits(r.whatsapp)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 hover:text-green-700"
                        >
                          <Phone className="w-4 h-4 shrink-0" />
                          {r.whatsapp}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_BADGE[r.status]}`}>
                      {statusLabel(r.status)}
                      {r.status === 'cancelled' && r.cancelledBy ? ` · ${cancelledByLabel(r.cancelledBy)}` : ''}
                    </span>
                    {r.status === 'confirmed' && new Date(r.startAt) > new Date() && (
                      <button
                        type="button"
                        onClick={() => handleCancelReserva(r)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        title={t('agendamentoAdmin.cancelReserva') as string}
                      >
                        <CalendarX className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminAgendamentos;
